<template>
    <div class="view refund-request-detail uk-padding">
        <router-link
            to="/refund-requests"
            class="uk-parent"
            active-class="uk-active"
        >
            <span uk-icon="icon: arrow-left" /> Refund Requests
        </router-link>

        <div class="refund-detail-page uk-container">
            <system-message
                :success="success"
                :message="message"
                class="uk-container"
            />

            <div v-if="refundRequest">
                <h4 style="color:black">
                    Refund #{{ refundRequest.id }} for job #{{ refundRequest.refundable_details.job_number }}
                </h4>
                <div v-if="refundRequest && refundRequest.refundable_details && refundRequest.refundable_details.buyer_name">
                    {{ refundRequest.refundable_details.buyer_name }}
                </div>
                <div v-if="refundRequest && refundRequest.refundable_details && refundRequest.refundable_details.site_address">
                    {{ refundRequest.refundable_details.site_address }}
                </div>

                <div class="uk-padding uk-padding-remove-left uk-padding-remove-right">
                    <div class="status-timeline">
                        <div
                            v-if="timelineStatuses.includes(refundRequest.status)"
                            class="uk-flex uk-flex-between"
                        >
                            <template
                                v-for="(status, index) in timelineStatuses"
                            >
                                <div
                                    v-if="index !=0"
                                    :key="'line' + index"
                                    class="line"
                                />
                                <div
                                    :key="index"
                                    class="status"
                                    :class="{active: status == refundRequest.status }"
                                >
                                    {{ status }}
                                </div>
                            </template>
                        </div>
                        <div
                            v-else
                            class="uk-flex uk-flex-between"
                        >
                            <div class="status active">
                                {{ refundRequest.status }}
                            </div>
                        </div>
                    </div>

                    <div class="status-mobile">
                        <div class="uk-flex uk-flex-between">
                            <div class="status active">
                                {{ refundRequest.status }}
                            </div>
                        </div>
                    </div>

                    <div
                        v-if="isUserRefundsAdmin && refundRequest.status !== 'Failed'"
                        class="uk-margin-small-top"
                    >
                        <button
                            type="button"
                            class="uk-button uk-button-link"
                            @click="showSetStatusToFailedModal"
                        >
                            Set status to Failed
                        </button>
                    </div>
                </div>

                <div class="uk-grid uk-padding-small uk-padding-remove-right uk-padding-remove-left">
                    <div class="uk-width-1-2@m uk-width-1-1@s">
                        <p>
                            {{ refundRequest.status_description }}
                        </p>

                        <field-value-list :list="summary1" />
                        <br>
                        <field-value-list :list="summary2" />
                        <br>
                        <div class="uk-flex left">
                            <div
                                v-if="statusIsSubmitted"
                                class="buttons uk-text-right uk-flex uk-flex-wrap uk-flex-right uk-margin-right"
                            >
                                <span
                                    v-if="showApproveRejectButtons"
                                    class="uk-flex uk-flex-right"
                                >
                                    <button
                                        type="button"
                                        class="uk-button uk-button-default uk-button-small uk-button-primary uk-margin-right"
                                        @click="approve"
                                    >
                                        Approve
                                    </button>
                                    <button
                                        type="button"
                                        class="uk-button uk-button-default uk-button-small uk-button-danger uk-margin-right"
                                        @click="reject"
                                    >
                                        Reject
                                    </button>
                                </span>
                            </div>
                            <div
                                v-else-if="statusIsPending"
                                class="buttons uk-text-right"
                            >
                                <span v-if="showVerifyButton">
                                    <button
                                        type="button"
                                        class="uk-button uk-button-default uk-button-small uk-button-primary uk-margin-right"
                                        @click="verify"
                                    >
                                        Verify
                                    </button>
                                </span>
                            </div>
                            <template v-if="showDownloadButton">
                                <div class="buttons uk-text-right">
                                    <button
                                        type="button"
                                        class="uk-button uk-button-default uk-button-small uk-button-primary uk-margin-right"
                                        @click="download"
                                    >
                                        Download
                                    </button>
                                </div>
                            </template>
                        </div>
                        <br>
                    </div>
                    <div class="uk-width-1-2@m uk-width-1-1@s">
                        <div v-if="refundRequest">
                            <refund-request-details-section :refund-request="refundRequest" />

                            <div
                                v-if="showCancelButton"
                                class="uk-text-right uk-text-small uk-padding uk-padding-remove-right"
                            >
                                <a @click="cancel">
                                    Cancel Refund Process
                                </a>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { approveRefundRequest, cancelRefundRequest, verifyRefundRequest, rejectRefundRequest, getSingleRefundRequest, downloadSingleRefundRequest, updateRefundRequestStatus } from "../helpers/api";
import { RefundStatuses } from "../helpers/constants";
import { showApproveOrRejectButtons, showCancelButton, showVerifyButton, showDownloadButton } from "../helpers/helpers";
import { updateLoader } from '@strangeanimals/common-vue-components/src/helpers/api';
const SystemMessage  = () => import("@strangeanimals/common-vue-components/src/components/page/SystemMessage");
const RefundRequestDetailsSection  = () => import("../components/generic/RefundRequestDetailsSection");
const FieldValueList  = () => import("../components/generic/FieldValueList");

export default {
    name: 'RefundRequestDetail',
    components: {SystemMessage, RefundRequestDetailsSection, FieldValueList},
    data() {
        return {
            token: null,
            refundRequest: null,
            success: true,
            message: [],
            refreshTimer: null,
            timelineStatuses: ['Submitted', 'Approved', 'Pending', 'Verified', 'Finalised'],
            summary1: {},
            summary2: {}
        }
    },
    computed: {
        ...mapGetters({
            user: 'user',
            canUserVerifyRefunds: 'canUserVerifyRefunds',
            canUserApproveRefunds: 'canUserApproveRefunds',
            isUserRefundsAdmin: 'isUserRefundsAdmin',
            apiAccessToken: 'apiAccessToken'
        }),
        statusIsSubmitted() {
            return this.refundRequest.status === RefundStatuses.SUBMITTED;
        },
        statusIsPending() {
            return this.refundRequest.status === RefundStatuses.PENDING;
        },
        showVerifyButton() {
            return showVerifyButton(this.refundRequest, true);
        },
        showDownloadButton() {
            return showDownloadButton(this.refundRequest, true);
        },
        showApproveRejectButtons() {
            return showApproveOrRejectButtons(this.refundRequest);
        },
        showCancelButton() {
            return showCancelButton(this.refundRequest);
        },
        statusOptions() {
            let options = [];
            for (let key in RefundStatuses) {
                options.push(RefundStatuses[key]);
            }
            options.sort();
            return options;
        }
    },
    watch: {
        refundRequest() {
            document.title = (this.refundRequest && this.refundRequest.refundable_details)
                ? this.refundRequest.refundable_details.job_number + ' - ' + this.refundRequest.refundable_details.salesforce_receipt_number
                : '';
        }
    },
    mounted() {
        this.token = this.$route.params.token;
        this.startTimer();
        this.getRefundRequest();
    },
    beforeDestroy () {
        this.cancelTimer();
    },
    methods: {
        getRefundRequest() {
            const self = this;
            this.resetTimer();
            getSingleRefundRequest(this.token)
                .then((response) => {
                    if (response.status === 200) {
                        self.refundRequest = response.body;
                        self.setSummary();
                    } else {
                        self.refundRequest = null;
                        self.setSystemMessage(["There was an error in fetching the refund request."], false);
                    }
                })
                .catch((response) => {
                    self.refundRequest = null;
                    if (response.status === 403) {
                        self.setSystemMessage(["You do not have permission view this refund request."], false);
                    } else {
                        self.setSystemMessage(["There was an error in fetching the refund request."], false);
                    }
                });
        },
        setSummary() {
            let summary1 = {};
            let summary1Fields = [
                'requested_by',
                'requested_date',
                'to_be_approved_by',
                'approval_reminder_email_sent_date',
            ];

            let fieldsToRename = {
                'approval_reminder_email_sent_date': 'reminder_sent'
            };

            summary1Fields.forEach((value) => {
                if (fieldsToRename.hasOwnProperty(value)) {
                    summary1[fieldsToRename[value]] = this.refundRequest[value];
                } else {
                    summary1[value] = this.refundRequest[value];
                }
            });

            let hiddenIfNullFields = [
                'cancelled_by',
                'cancelled_date',
                'approved_by',
                'approved_date',
                'accounts_team_email_sent_date',
                'verified_by',
                'verified_date'
            ];

            hiddenIfNullFields.forEach((value) => {
                if (this.refundRequest[value] != null) {
                    summary1[value] = this.refundRequest[value];
                }
            });

            let summary2 = {
                'salesforce_receipt_number' : this.refundRequest.refundable_details['salesforce_receipt_number'],
                'amount' : this.refundRequest.refundable_details['amount'],
                'payment_method' : this.refundRequest['payment_method'],
                'salesforce_id' : this.refundRequest['salesforce_id'],
            };

            this.summary1 = summary1;
            this.summary2 = summary2;
        },
        setSystemMessage(message, isSuccess) {
            this.message = message;
            this.success = isSuccess;
        },
        verify() {
            const self = this;
            let message = 'Are you sure you want to mark this refund request as verified?';
            this.$uikit.modal.confirm(message)
                .then(function() {
                    self.verifyRefund(self.token);
                }, function() {
                    self.$uikit.modal.hide();
                });
        },
        approve() {
            const self = this;
            this.$uikit.modal.confirm('Are you sure you want to approve this refund request?')
                .then(function() {
                    self.approveRefund(self.token);
                }, function() {
                    this.$uikit.modal.hide();
                });
        },
        reject() {
            const self = this;
            this.$uikit.modal.confirm('Are you sure you want to reject this refund request?')
                .then(function() {
                    self.rejectRefund(self.token);
                }, function() {
                    self.$uikit.modal.hide();
                });
        },
        cancel() {
            const self = this;
            this.$uikit.modal.confirm('Are you sure you want to cancel this refund request?')
                .then(function() {
                    self.cancelRefund(self.token);
                }, function() {
                    self.$uikit.modal.hide();
                });
        },
        showSetStatusToFailedModal() {
            const self = this;
            this.$uikit.modal.confirm('Are you sure you want to set the status to Failed?')
                .then(function() {
                    self.setStatusToFailed(self.token);
                }, function() {
                    self.$uikit.modal.hide();
                });
        },
        download() {
            const self = this;
            let refundRequest = this.refundRequest;
            updateLoader(1, true);
            downloadSingleRefundRequest(refundRequest.token, self.apiAccessToken)
                .then(resp => resp.blob())
                .then(blob => {
                    updateLoader(-1, true);
                    const url = window.URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.style.display = 'none';
                    a.download = refundRequest.refundable_details.job_number + '_'  + refundRequest.refundable_details.salesforce_receipt_number + '.pdf';
                    a.href = url;
                    document.body.appendChild(a);
                    a.click();
                    window.URL.revokeObjectURL(url);
                })
                .catch((response) => {
                    updateLoader(-1, true);
                    self.setSystemMessage(['An error occurred while trying to download the file.'], false);
                });
        },
        refresh() {
            this.getRefundRequest();
        },
        preparePayload(token) {
            return {
                token: token
            }
        },
        handleCode200Result(self, response, action, status) {
            let notUpdatedCount = Object.keys(response['not_updated_tokens']).length;
            if (notUpdatedCount > 0) {
                self.setSystemMessage(["An error was encountered while trying to " + action + " this refund request."], false);
            } else {
                self.setSystemMessage(["Refund request has been " + status], true);
            }
            self.refresh();
        },
        verifyRefund(token) {
            const self = this;
            const params = {
                token: token
            };
            verifyRefundRequest(params)
                .then((response) => {
                    if (response.status === 200) {
                        self.handleCode200Result(self, response, 'verify', 'marked as verified');
                    } else {
                        self.setSystemMessage(["An error was encountered while trying to verify this refund request."], false);
                    }
                })
                .catch((response) => {
                    if (response.status === 403) {
                        self.setSystemMessage(["You do not have permission to verify the refund."], false);
                    } else {
                        self.setSystemMessage(["An error was encountered while trying to verify this refund request."], false);
                    }
                });
        },
        approveRefund(token) {
            const self = this;
            const params = this.preparePayload(token);
            approveRefundRequest(params)
                .then((response) => {
                    if (response.status === 200) {
                        self.handleCode200Result(self, response, 'approve', 'approved');
                    } else {
                        self.setSystemMessage(["An error was encountered while trying to approve this refund request."], false);
                    }
                })
                .catch((response) => {
                    if (response.status === 403) {
                        self.setSystemMessage(["You do not have permission to approve the refund."], false);
                    } else {
                        self.setSystemMessage(["An error was encountered while trying to approve this refund request."], false);
                    }
                });
        },
        rejectRefund(token) {
            const self = this;
            const params = this.preparePayload(token);
            rejectRefundRequest(params)
                .then((response) => {
                    if (response.status === 200) {
                        self.handleCode200Result(self, response, 'reject', 'rejected');
                    } else {
                        self.setSystemMessage(["An error was encountered while trying to reject this refund request."], false);
                    }
                })
                .catch((response) => {
                    if (response.status === 403) {
                        self.setSystemMessage(["You do not have permission to reject the refund."], false);
                    } else {
                        self.setSystemMessage(["An error was encountered while trying to reject this refund request."], false);
                    }
                });
        },
        cancelRefund(token) {
            const self = this;
            const params = this.preparePayload(token);
            cancelRefundRequest(params)
                .then((response) => {
                    if (response.status === 200) {
                        self.handleCode200Result(self, response, 'cancel', 'cancelled');
                    } else {
                        self.setSystemMessage(["An error was encountered while trying to cancel this refund request."], false);
                    }
                })
                .catch((response) => {
                    if (response.status === 403) {
                        self.setSystemMessage(["You do not have permission to cancel the refund."], false);
                    } else {
                        self.setSystemMessage(["An error was encountered while trying to cancel this refund request."], false);
                    }
                });
        },
        setStatusToFailed (token) {
            const self = this;
            const params = {
                token: token,
                status: 'Failed'
            };
            updateRefundRequestStatus(params)
                .then((response) => {
                    if (response.status === 200) {
                        self.setSystemMessage(["Refund request status has been updated."], true);
                        self.refresh();
                    } else {
                        self.setSystemMessage(["An error was encountered while trying to update this refund request."], false);
                    }
                })
                .catch((response) => {
                    if (response.status === 403) {
                        self.setSystemMessage(["You do not have permission to update the refund."], false);
                    } else {
                        self.setSystemMessage(["An error was encountered while trying to update this refund request."], false);
                    }
                });
        },
        startTimer() {
            let mins = 10;
            let interval = mins * 60 * 1000;
            this.refreshTimer = setInterval(this.getRefundRequest, interval);
        },
        resetTimer () {
            this.cancelTimer();
            this.startTimer();
        },
        cancelTimer () {
            clearInterval(this.refreshTimer);
        }
    }
}
</script>

<style>
    .refund-detail-page {
        padding: 30px;
    }

    .status-timeline {
        display: block;
    }

    .status-mobile {
        display: none;
    }

    @media screen and (max-width: 640px) {
        .refund-detail-page {
            padding: 15px;
        }

        .status-timeline {
            display: none;
        }

        .status-mobile {
            display: block;
        }
    }

    div .status {
        border: 1px solid #e0e0e0;
        border-radius: 500px;
        padding: 5px 25px;
    }

    div .status.active {
        background-color: #e0e0e0;
    }

    .line {
        border: 0;
        border-top: 1px solid #ccc;
        display: block;
        height: 1px;
        margin: 1em 0;
        padding: 0;
        width: 100%;
    }

    .refund-request-detail {
        color: black !important;
    }

</style>
